import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, Textarea, TextInput, LoadingOverlay, Container } from '@mantine/core';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import { useDisclosure } from '@mantine/hooks';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { masterGroupService } from 'services/lia/masterGroupService';
import { API_ENDPOINTS } from 'services/api';
import httpClient from 'services/httpClient';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const items = [
  { title: 'Master Group', href: '/app/lia/questions/group-questions' },
  { title: 'New Group', href: '' }
];

const groupSchema = z.object({
  title: z.string().nonempty('Name is required'),
  desc: z.string().nonempty('Description is required'),
  moduleID: z.string().nonempty('Module is required')
});

type FormData = z.infer<typeof groupSchema>;

const AddMasterGroupPage: React.FC = () => {
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  /* eslint-disable */
  // Fetch modules
  const { data: modulesData, isLoading: isLoadingModules } = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      try {
        const response = await httpClient.get(API_ENDPOINTS.MODULE.LIST);
        return response.data;
      } catch {
        return { data: [] };
      }
    }
  });
  /* eslint-enable */

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: FormData) => masterGroupService.create(data),
    onSuccess: () => {
      navigate('/app/lia/questions/group-questions');
      showSuccessNotification({
        title: 'Created successfully!',
        message: 'Your group has been created successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to created group',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      moduleID: '1'
    }
  });

  const onSubmit = async (data: FormData) => {
    setFormData(data);
    open();
  };

  const processData = async () => {
    if (formData) {
      createMutation.mutate(formData);
    }
    close();
  };

  return (
    <Container fluid>
      <PageTitle title='Add Group' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Submit'
        statusProcess='submit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processData}
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={isLoadingModules || createMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            <Controller
              name='title'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Name'
                  placeholder='Input group name...'
                  required
                  error={errors.title?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='desc'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Group Description'
                  description='The description of the group'
                  placeholder='Input description...'
                  required
                  error={errors.desc?.message}
                  {...field}
                />
              )}
            />
          </Stack>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px'
            }}
          >
            <SecondaryButton
              text='Cancel'
              mr={10}
              onClick={() => navigate('/app/lia/questions/group-questions')}
            />
            <PrimaryButton
              text='Save'
              type='submit'
              loading={isSubmitting || createMutation.isPending}
            />
          </div>
        </form>
      </div>
    </Container>
  );
};

export default AddMasterGroupPage;
